import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { memo, useState } from 'react';
import { XLarge } from '@outdoor-voices/ui';
import cc from 'classcat';
import PropTypes from 'prop-types';
import { ERROR_STATUSES } from './constants';
import Check from '../icons/Check';
import css from './ProgressBar.module.scss';
import useTimeout from '../../utils/hooks/useTimeout';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var BAR_DELAY = 500; // delay before animation

var READY_FOR_PICKUP = 'available_for_pickup';
var PICKUP_MESSAGE = 'Pickup Ready'; // status progress states in order

var STATUS_BAR_STATES = [{
  message: 'Order Placed',
  progress: '0%',
  statuses: []
}, {
  message: 'Processing',
  progress: '25%',
  statuses: ['unknown', 'pre_transit']
}, {
  message: 'Shipped',
  progress: '50%',
  statuses: ['in_transit']
}, {
  message: 'Out For Delivery',
  progress: '75%',
  statuses: ['out_for_delivery']
}, {
  message: 'Delivered',
  progress: '100%',
  statuses: ['delivered']
}];
var STATUS_BAR_STATES_BOPIS = [{
  message: 'Order Placed',
  progress: '0%',
  statuses: []
}, {
  message: 'Processing',
  progress: '50%',
  statuses: ['unknown', 'pre_transit']
}, {
  message: 'Picked Up',
  pickupMessage: PICKUP_MESSAGE,
  progress: '100%',
  statuses: ['fulfilled']
}];
var CANCELLED = 'cancelled';
var CANCELLED_STATES = [{
  message: 'Order Placed',
  progress: '0%',
  statuses: []
}, {
  message: 'Order Cancelled',
  progress: '33%',
  statuses: ['cancelled'],
  isError: true
}];

var ErrorMessage = function ErrorMessage() {
  return /*#__PURE__*/_jsxs("p", {
    children: ['Something went wrong, please ', /*#__PURE__*/_jsx("a", {
      href: "/pages/contact-us",
      children: "contact us"
    }), ' for help.']
  });
};

var ERROR_STATES = [{
  message: 'Order Placed',
  progress: '0%',
  statuses: []
}, {
  message: /*#__PURE__*/_jsx(ErrorMessage, {}),
  progress: '33%',
  statuses: ERROR_STATUSES,
  isError: true
}];

var ProgressBar = function ProgressBar(_ref) {
  var bopis = _ref.bopis,
      fulfillmentStatus = _ref.fulfillmentStatus,
      hasOrderError = _ref.hasOrderError,
      loading = _ref.loading,
      initialStatus = _ref.status;
  var status = bopis ? fulfillmentStatus : initialStatus;

  var _useState = useState(-1),
      _useState2 = _slicedToArray(_useState, 2),
      index = _useState2[0],
      setIndex = _useState2[1];

  var canPickup = status === READY_FOR_PICKUP;
  var isCancelled = status === CANCELLED;
  var statusBarStates = isCancelled ? CANCELLED_STATES : STATUS_BAR_STATES;

  if (bopis) {
    statusBarStates = STATUS_BAR_STATES_BOPIS;
  }

  if (hasOrderError) {
    statusBarStates = ERROR_STATES;
  }

  useTimeout(function () {
    if (loading) {
      return;
    }

    var newIndex = statusBarStates.findIndex(function (_ref2) {
      var statuses = _ref2.statuses;
      return statuses.indexOf(status) > -1;
    });
    if (newIndex < 0) newIndex = 0;
    setIndex(newIndex);
  }, BAR_DELAY, [loading, status]);
  var progress = statusBarStates[index > -1 ? index : 0].progress;
  return /*#__PURE__*/_jsxs("div", {
    className: css.root,
    children: [/*#__PURE__*/_jsx("div", {
      className: css.barBg,
      children: /*#__PURE__*/_jsx("div", {
        className: cc([css.barFg, _defineProperty({}, css.redBar, hasOrderError), _defineProperty({}, css.grayBar, isCancelled)]),
        style: {
          width: progress
        }
      })
    }), /*#__PURE__*/_jsx("ul", {
      className: css.statuses,
      children: statusBarStates.map(function (_ref5, statusIndex) {
        var isError = _ref5.isError,
            message = _ref5.message,
            pickupMessage = _ref5.pickupMessage;
        return /*#__PURE__*/_jsxs("li", {
          className: cc([css.status, _defineProperty({}, css.statusOn, !loading && statusIndex === index), _defineProperty({}, css.isError, isError), _defineProperty({}, css.redIcon, isError && hasOrderError), _defineProperty({}, css.showIcon, !loading && statusIndex <= index)]),
          children: [isError ? /*#__PURE__*/_jsx(XLarge, {}) : /*#__PURE__*/_jsx(Check, {}), canPickup && pickupMessage ? pickupMessage : message]
        }, message);
      })
    })]
  });
};

ProgressBar.defaultProps = {
  hasOrderError: false
};
ProgressBar.propTypes = {
  bopis: PropTypes.bool,
  fulfillmentStatus: PropTypes.string,
  hasOrderError: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  status: PropTypes.string.isRequired
};
export default /*#__PURE__*/memo(ProgressBar);